import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, normalizeClass as _normalizeClass, normalizeProps as _normalizeProps, guardReactiveProps as _guardReactiveProps } from "vue"

const _hoisted_1 = { class: "search-modal__header--title" }
const _hoisted_2 = {
  key: 0,
  class: "search-modal__filter-input-container"
}
const _hoisted_3 = ["placeholder"]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = { class: "search-modal__footer" }
const _hoisted_6 = {
  key: 1,
  class: "search-modal__button-row"
}
const _hoisted_7 = ["aria-label", "disabled"]
const _hoisted_8 = ["disabled", "aria-label"]

import { ref, onMounted, computed } from 'vue';
import Modal from '@/js/modules/modal';
import DropdownClearButton from '@lmt-rpb/DropdownClearButton/DropdownClearButton.vue';
import { useTranslations } from '@helper/composables/translations';
import FaIcon from '@lmt-rpb/Atoms/FaIcon/FaIcon.vue';
import { vTouchTarget } from 'src/directives/TouchTarget/touchTarget';
import { handleInputFocusWithCursorPositionEnd } from '@/js/utils/input';

type Props = {
	header: string;
	applyButton?: string;
	cancelButton?: string;
	resetInsteadOfCancel?: boolean;
	searchable?: boolean;
	withClearButton?: boolean;
	inputClearable?: boolean;
	isFocused?: boolean;
	clearDisabled?: boolean;
	buttonDisabled?: boolean;
	clearOnOpen?: boolean;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'SearchModal',
  props: /*@__PURE__*/_mergeModels({
    header: {},
    applyButton: { default: '' },
    cancelButton: { default: '' },
    resetInsteadOfCancel: { type: Boolean },
    searchable: { type: Boolean },
    withClearButton: { type: Boolean },
    inputClearable: { type: Boolean },
    isFocused: { type: Boolean },
    clearDisabled: { type: Boolean },
    buttonDisabled: { type: Boolean },
    clearOnOpen: { type: Boolean, default: true }
  }, {
    "modelValue": {},
    "modelModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels([
	'SearchModal:close',
	'SearchModal:applyChanges',
	'SearchModal:cancel',
	'SearchModal:clear',
	'SearchModal:clearInput',
	'SearchModal:focusedIn',
	'SearchModal:focusedOut',
], ["update:modelValue"]),
  setup(__props: any, { expose: __expose, emit: __emit }) {

const { translate } = useTranslations();
const modalRef = ref<HTMLDialogElement | null>(null);
let modalInstance: Modal | null = null;

const input = ref<HTMLInputElement>();

const props = __props;

const emit = __emit;
const filterExpression = _useModel<string>(__props, "modelValue");

const handleInputFocus = (event: FocusEvent) => {
	emit('SearchModal:focusedIn');
	handleInputFocusWithCursorPositionEnd(event);
};

onMounted(() => {
	modalInstance = new Modal(modalRef.value!, window.matchMedia('(min-width: 1300px)'), {
		onBeforeClose: () => emit('SearchModal:close'),
	});
	if (window.innerWidth > 1300) {
		modalInstance?.closeModal();
	}
});

const showButtonRow = computed(() => props.applyButton || props.cancelButton);

const clearInput = () => {
	if (!props.inputClearable) return;
	filterExpression.value = '';
};

const openModal = () => {
	if (window.innerWidth <= 1300) {
		modalInstance?.openModal();
		if (props.clearOnOpen) {
			filterExpression.value = '';
		}
	}
};

const handleApply = () => {
	if (props.buttonDisabled) {
		return;
	}
	emit('SearchModal:applyChanges');
	modalInstance?.closeModal();
};

const evaluateCloseOrReset = (isReset?: boolean) => {
	if (props.resetInsteadOfCancel) {
		emit('SearchModal:clear', isReset);
	} else {
		handleClose();
	}
};

const handleClose = () => {
	modalInstance?.closeModal();
};

__expose({ openModal, handleClose, input });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("dialog", {
    ref_key: "modalRef",
    ref: modalRef,
    class: "search-modal"
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["search-modal__content", { 'search-modal__content--searchable': _ctx.searchable }])
    }, [
      _createElementVNode("div", {
        class: "search-modal__header",
        onClick: handleClose
      }, [
        _createElementVNode("span", _hoisted_1, _toDisplayString(_ctx.header), 1 /* TEXT */),
        _createVNode(FaIcon, {
          icon: ['fa-solid', 'fa-xmark'],
          class: "search-modal__header--close"
        })
      ]),
      (_ctx.searchable)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _withDirectives(_createElementVNode("input", {
              ref_key: "input",
              ref: input,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filterExpression).value = $event)),
              class: "search-modal__filter-input",
              type: "text",
              placeholder: _unref(translate)('destination.modal.searchInput'),
              autofocus: "",
              onFocusin: handleInputFocus,
              onFocusout: _cache[1] || (_cache[1] = () => emit('SearchModal:focusedOut'))
            }, null, 40 /* PROPS, NEED_HYDRATION */, _hoisted_3), [
              [_vModelText, filterExpression.value]
            ]),
            _createVNode(FaIcon, {
              class: "search-modal__filter-input-icon",
              icon: ['fa-solid', 'fa-magnifying-glass']
            }),
            (_ctx.inputClearable && filterExpression.value?.length)
              ? _withDirectives((_openBlock(), _createElementBlock("button", {
                  key: 0,
                  type: "button",
                  "aria-label": _unref(translate)('ariaLabel.clearSearch'),
                  class: "search-modal__filter-input-clear-wrapper",
                  onClick: clearInput
                }, [
                  (_ctx.inputClearable && filterExpression.value?.length)
                    ? (_openBlock(), _createBlock(FaIcon, {
                        key: 0,
                        class: "search-modal__filter-input-clear-icon",
                        icon: ['fa-solid', 'fa-circle-xmark'],
                        onClick: clearInput
                      }))
                    : _createCommentVNode("v-if", true)
                ], 8 /* PROPS */, _hoisted_4)), [
                  [
                    _unref(vTouchTarget),
                    void 0,
                    void 0,
                    { selfManaged: true }
                  ]
                ])
              : _createCommentVNode("v-if", true)
          ]))
        : _createCommentVNode("v-if", true),
      _createElementVNode("div", {
        class: _normalizeClass(["search-modal__main", { 'is-focused': _ctx.isFocused }])
      }, [
        _renderSlot(_ctx.$slots, "default")
      ], 2 /* CLASS */),
      _createElementVNode("div", _hoisted_5, [
        (_ctx.withClearButton)
          ? (_openBlock(), _createBlock(DropdownClearButton, {
              key: 0,
              class: "search-modal__clear-button",
              onClick: _cache[2] || (_cache[2] = ($event: any) => (emit('SearchModal:clear')))
            }))
          : _createCommentVNode("v-if", true),
        (showButtonRow.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
              _renderSlot(_ctx.$slots, "footer", _normalizeProps(_guardReactiveProps({ evaluateCloseOrReset: evaluateCloseOrReset, handleApply: handleApply })), () => [
                (_ctx.cancelButton)
                  ? (_openBlock(), _createElementBlock("button", {
                      key: 0,
                      "aria-label": 
								props.resetInsteadOfCancel ? _unref(translate)('ariaLabel.resetFilter') : _unref(translate)('ariaLabel.cancelChanges')
							,
                      class: _normalizeClass(["search-modal__button search-modal__button--cancel", { 'reset-styling': props.resetInsteadOfCancel, 'is-disabled': _ctx.clearDisabled }]),
                      disabled: _ctx.clearDisabled,
                      type: "button",
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (evaluateCloseOrReset(true)))
                    }, _toDisplayString(_ctx.cancelButton), 11 /* TEXT, CLASS, PROPS */, _hoisted_7))
                  : _createCommentVNode("v-if", true),
                _createElementVNode("button", {
                  type: "button",
                  disabled: _ctx.buttonDisabled,
                  "aria-label": _unref(translate)('ariaLabel.applyChanges'),
                  class: _normalizeClass(["search-modal__button search-modal__button--apply", { 'is-disabled': _ctx.buttonDisabled }]),
                  onClick: handleApply
                }, _toDisplayString(_ctx.applyButton), 11 /* TEXT, CLASS, PROPS */, _hoisted_8)
              ])
            ]))
          : _createCommentVNode("v-if", true)
      ])
    ], 2 /* CLASS */)
  ], 512 /* NEED_PATCH */))
}
}

})